import { useEffect, useState } from "react";
import { PiVideoCamera, PiWarningCircle } from "react-icons/pi";

import useUiContext from "@/custolink-hooks/useUiContext";
import type { PublicSupportVideoCallsApi$EnduserRespondResult } from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import { normaliseHex } from "@/custolink-views/asset_frontend/redesigns/theme";
import { usePreviewFonts } from "@/custolink-views/asset_frontend/redesigns/fonts";
import { AssetShell, PreviewHeader } from "@/custolink-views/asset_frontend/redesigns/tileKit";
import { ErrorNote, PrimaryButton } from "@/custolink-views/asset_frontend/redesigns/formKit";

/**
 * Height, in px, of the dev/staging `EnvironmentBanner` currently on screen, or 0 when none
 * is rendered (production leaves it unset). The banner lives outside this page's own shell
 * (`ContextAndAuthLoader`), so a plain `min-h-dvh` on the content below it is one viewport
 * tall starting *after* the banner - it overflows the bottom of the screen by the banner's
 * height and the join card ends up centered a little low. Measuring it and subtracting it
 * keeps the card centered in whatever is actually visible, banner or not.
 */
function useBannerHeight() {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('[role="note"]');
    if (el == null) return;
    const update = () => setHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return height;
}

/**
 * VideoByLink, asset frontend design: the landing / join / left / rejoin /
 * ended / error screens an SMS invite link opens on, before a call is
 * joined. Behaviour, polling and the join/rejoin handlers all live in
 * VideoByLink.tsx, unchanged; this only re-skins the states. The in-call
 * room (MobileTechnicianRoom / classic VideoRoom) is a separate switch in
 * VideoByLink.tsx and never reaches this component.
 *
 * Mostly opened from an SMS link on a phone, so this is mobile-first: one
 * centered card on the brand ground, a single full-width primary action.
 */
export default function VideoByLinkDesign(p: {
  isPending: boolean;
  isError: boolean;
  data: PublicSupportVideoCallsApi$EnduserRespondResult | undefined;
  finished: boolean;
  left: boolean;
  onJoin: () => void;
  onRejoin: () => void;
}) {
  const uic = useUiContext();
  usePreviewFonts(["schibsted"]);
  const bannerHeight = useBannerHeight();

  const lf = uic.organisationContext.lookAndFeel;
  const accent = normaliseHex(lf.assetFrontendBgcolorHex);

  return (
    <AssetShell accent={accent}>
      <div
        className="flex min-h-dvh flex-col"
        style={{ minHeight: `calc(100dvh - ${bannerHeight}px)` }}
      >
        <PreviewHeader logoUrl={lf.logoUrl} />

        <div className="flex flex-1 flex-col items-center justify-center px-4 py-6">
          <div className="w-full rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] p-5 text-center text-[var(--rda-on-surface)] sm:p-6">
            {p.isPending ? <PendingState /> : null}
            {!p.isPending && p.isError ? <ErrorState /> : null}
            {!p.isPending && !p.isError && p.data != null && p.finished ? <FinishedState /> : null}
            {!p.isPending && !p.isError && p.data != null && !p.finished && p.left ? (
              <LeftState onRejoin={p.onRejoin} />
            ) : null}
            {!p.isPending && !p.isError && p.data != null && !p.finished && !p.left ? (
              <LandingState data={p.data} onJoin={p.onJoin} />
            ) : null}
          </div>
        </div>
      </div>
    </AssetShell>
  );
}

function PendingState() {
  return (
    <div className="flex flex-col items-center gap-3 py-4">
      <span className="h-8 w-8 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent text-[var(--rda-accent-text)] opacity-70" />
      <p className="text-sm text-[var(--rda-muted)]">Opening the video call...</p>
    </div>
  );
}

function ErrorState() {
  return (
    <ErrorNote>
      <span className="flex items-center justify-center gap-2 text-center">
        <PiWarningCircle aria-hidden className="shrink-0 text-lg" />
        Error. The video could not be opened. The video chat may have been closed.
      </span>
    </ErrorNote>
  );
}

function FinishedState() {
  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-lg font-semibold tracking-[-0.01em] sm:text-xl">
        Video call has been closed
      </h1>
      <p className="text-sm text-[var(--rda-muted)]">The video call has ended.</p>
    </div>
  );
}

function LeftState(p: { onRejoin: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold tracking-[-0.01em] sm:text-xl">
          You have left the call
        </h1>
        <p className="text-sm text-[var(--rda-muted)]">
          The call is still open. You can rejoin if you need to.
        </p>
      </div>
      <PrimaryButton className="w-full max-w-xs" onClick={p.onRejoin}>
        Rejoin
      </PrimaryButton>
    </div>
  );
}

function LandingState(p: {
  data: PublicSupportVideoCallsApi$EnduserRespondResult;
  onJoin: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4">
      <span
        aria-hidden
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--rda-accent-tint)] text-[var(--rda-accent-text)]"
      >
        <PiVideoCamera className="text-2xl" />
      </span>
      <div className="flex flex-col gap-1.5">
        <h1 className="text-lg font-semibold tracking-[-0.01em] sm:text-xl">
          {p.data.organisationName}
        </h1>
        <p className="text-sm text-[var(--rda-on-surface)]">
          {p.data.callerDisplayName} is waiting for you on a video call, or will join shortly.
        </p>
        <p className="text-xs text-[var(--rda-muted)]">
          Your camera and microphone are used for this call.
        </p>
      </div>
      <PrimaryButton className="w-full max-w-xs" onClick={p.onJoin}>
        Join video call
      </PrimaryButton>
    </div>
  );
}
